import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Trash2,
  Building2,
  Phone,
  UserCog,
  Flag,
  RefreshCw,
  ShieldAlert,
  ChevronRight,
  ChevronDown,
  Copy,
  Share2,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import GarbageSection from "@/components/cleanup/GarbageSection";
import BrokerageSection from "@/components/cleanup/BrokerageSection";
import BrokerageDupesSection from "@/components/cleanup/BrokerageDupesSection";
import PhoneSection from "@/components/cleanup/PhoneSection";
import NameDupesSection from "@/components/cleanup/NameDupesSection";
import NamesSection from "@/components/cleanup/NamesSection";
import ManualSection from "@/components/cleanup/ManualSection";
import SocialLinksSection from "@/components/cleanup/SocialLinksSection";
import SocialDupesSection from "@/components/cleanup/SocialDupesSection";

const ACCENT_CLASSES = {
  rose: "bg-rose-50 text-rose-700",
  blue: "bg-blue-50 text-blue-700",
  teal: "bg-teal-50 text-teal-700",
  amber: "bg-amber-50 text-amber-700",
  emerald: "bg-emerald-50 text-emerald-700",
  violet: "bg-violet-50 text-violet-700",
  slate: "bg-slate-50 text-slate-700",
  sky: "bg-sky-50 text-sky-700",
  orange: "bg-orange-50 text-orange-700",
};

const SECTION_DEFS = [
  { key: "garbage", label: "1 · Garbage purge", icon: Trash2, accent: "rose" },
  { key: "brokerage", label: "2 · Brokerage normalization", icon: Building2, accent: "blue" },
  { key: "brokerage_dupes", label: "3 · Brokerage duplicates", icon: Building2, accent: "teal" },
  { key: "phone", label: "4 · Phone-based dedupe", icon: Phone, accent: "amber" },
  { key: "name_dupes", label: "5 · Realtor duplicates (same name)", icon: Copy, accent: "emerald" },
  { key: "names", label: "6 · Name parsing fixes", icon: UserCog, accent: "violet" },
  { key: "manual", label: "7 · Manual outliers", icon: Flag, accent: "slate" },
  { key: "social_links", label: "8a · Social links", icon: Share2, accent: "sky" },
  { key: "social_dupes", label: "8b · Duplicate social profiles", icon: Users, accent: "orange" },
];

export default function Cleanup() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openSection, setOpenSection] = useState(null);
  const [socialLinksCount, setSocialLinksCount] = useState(0);
  const [socialDupesCount, setSocialDupesCount] = useState(0);

  async function loadLatest() {
    try {
      const { data } = await api.get(`/cleanup/audit/latest`);
      if (!data?.empty) setAudit(data);
    } catch (e) {
      // ignore
    }
  }

  // Fetch social_links count from the dirty-count breakdown (not in audit snapshot)
  async function loadSocialCount() {
    try {
      const { data } = await api.get("/cleanup/dirty-count");
      setSocialLinksCount(data?.breakdown?.social_links || 0);
      setSocialDupesCount(data?.breakdown?.social_dupes || 0);
    } catch (_) { /* silent */ }
  }

  async function runAudit(silent = false) {
    setLoading(true);
    try {
      const { data } = await api.post(`/cleanup/audit/run`, {});
      setAudit(data);
      loadSocialCount(); // refresh social_links badge
      // Signal the sidebar badge (and any other listener) to re-fetch the
      // dirty count so it drops the moment we merge / apply fixes without
      // waiting for the 60s poll tick.
      try {
        window.dispatchEvent(new Event("cleanup-audit-updated"));
      } catch (_) { /* older browsers */ }
      if (!silent) toast.success("Audit complete");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLatest().then(() => {
      // If nothing on file, run a fresh one
      if (!audit) runAudit(true);
    });
    loadSocialCount();
    // eslint-disable-next-line
  }, []);

  const counts = audit?.counts || {};
  const samples = audit?.samples || {};

  return (
    <div className="space-y-6" data-testid="cleanup-page">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Cleanup</h1>
          <p className="text-sm text-slate-500 mt-1">
            Nine-step data quality pass. Every destructive action shows a preview first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/cleanup/log"
            className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900"
            data-testid="cleanup-log-link"
          >
            <ShieldAlert className="h-4 w-4" /> Audit log
          </Link>
          <Button
            variant="outline"
            onClick={() => runAudit(false)}
            disabled={loading}
            data-testid="cleanup-rerun-audit"
            className="min-h-[44px]"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Scanning…" : "Re-run audit"}
          </Button>
        </div>
      </header>

      {!audit && (
        <Card className="p-6 text-center text-slate-500">
          {loading ? "Running first scan…" : "No audit yet. Click Re-run audit."}
        </Card>
      )}

      {audit && (
        <>
          <SummaryStrip counts={counts} total={audit.total_realtors} runAt={audit.run_at} />

          {SECTION_DEFS.map((s) => {
            const Icon = s.icon;
            const isOpen = openSection === s.key;
            const summary = sectionSummary(s.key, counts);
            const badgeCount = sectionCount(s.key, counts, socialLinksCount, socialDupesCount);
            return (
              <Card key={s.key} className="overflow-hidden" data-testid={`cleanup-section-${s.key}`}>
                <button
                  onClick={() => setOpenSection(isOpen ? null : s.key)}
                  className="w-full text-left flex items-center gap-4 p-4 sm:p-5 hover:bg-slate-50 transition min-h-[64px]"
                  data-testid={`cleanup-section-toggle-${s.key}`}
                >
                  <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md ${ACCENT_CLASSES[s.accent]}`}>
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900">{s.label}</div>
                    <div className="text-sm text-slate-500 mt-0.5 truncate">{summary}</div>
                  </div>
                  {badgeCount != null && badgeCount > 0 ? (
                    <span
                      className="inline-flex items-center justify-center rounded-full bg-rose-600 text-white text-[10px] font-semibold leading-none min-w-[20px] h-[20px] px-1.5 shrink-0"
                      data-testid={`cleanup-section-badge-${s.key}`}
                    >
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  ) : null}
                  {isOpen ? <ChevronDown className="h-5 w-5 text-slate-400 shrink-0" /> : <ChevronRight className="h-5 w-5 text-slate-400 shrink-0" />}
                </button>
                {isOpen && (
                  <div className="border-t border-slate-200 p-4 sm:p-5 bg-slate-50/50">
                    {s.key === "garbage" && <GarbageSection samples={samples} counts={counts} onChange={() => runAudit(true)} />}
                    {s.key === "brokerage" && <BrokerageSection onChange={() => runAudit(true)} />}
                    {s.key === "brokerage_dupes" && <BrokerageDupesSection onChange={() => runAudit(true)} />}
                    {s.key === "phone" && <PhoneSection onChange={() => runAudit(true)} />}
                    {s.key === "name_dupes" && <NameDupesSection onChange={() => runAudit(true)} />}
                    {s.key === "names" && <NamesSection counts={counts} onChange={() => runAudit(true)} />}
                    {s.key === "manual" && <ManualSection onChange={() => runAudit(true)} />}
                    {s.key === "social_links" && <SocialLinksSection onChange={() => runAudit(true)} />}
                    {s.key === "social_dupes" && <SocialDupesSection onChange={() => { runAudit(true); loadSocialCount(); }} onCountChange={setSocialDupesCount} />}
                  </div>
                )}
              </Card>
            );
          })}
        </>
      )}
    </div>
  );
}

function sectionCount(key, counts, socialLinksCount, socialDupesCount) {
  switch (key) {
    case "garbage": return (counts.garbage_email_rows || 0) + (counts.garbage_test_brokerage_rows || 0);
    case "brokerage": return counts.brokerage_normalization_groups || 0;
    case "brokerage_dupes": return null; // dynamic — loaded when section opens
    case "phone": return counts.phone_dupe_groups || 0;
    case "name_dupes": return counts.name_dupe_groups || 0;
    case "names": return (counts.name_nickname||0)+(counts.name_business_suffix||0)+(counts.name_all_caps||0)+(counts.name_designation_tail||0)+(counts.name_punct||0);
    case "manual": return counts.manual_outliers || 0;
    case "social_links": return socialLinksCount || 0;
    case "social_dupes": return socialDupesCount || 0;
    default: return null;
  }
}

function sectionSummary(key, c) {
  switch (key) {
    case "garbage": {
      const email = c.garbage_email_rows || 0;
      const test = c.garbage_test_brokerage_rows || 0;
      return `${email} invalid-email rows · ${test} test-brokerage realtors`;
    }
    case "brokerage": {
      const g = c.brokerage_normalization_groups || 0;
      const r = c.brokerage_normalization_affected_realtors || 0;
      return `${g} normalization group${g !== 1 ? "s" : ""} · ${r} realtor${r !== 1 ? "s" : ""} affected`;
    }
    case "brokerage_dupes":
      return "Detect & merge duplicate brokerage records (same name + address)";
    case "phone": {
      const g = c.phone_dupe_groups || 0;
      const r = c.phone_dupe_affected_realtors || 0;
      return `${g} duplicate group${g !== 1 ? "s" : ""} · ${r} realtor${r !== 1 ? "s" : ""} sharing a phone`;
    }
    case "name_dupes": {
      const g = c.name_dupe_groups || 0;
      const r = c.name_dupe_affected_realtors || 0;
      return `${g} duplicate pair${g !== 1 ? "s" : ""} · ${r} realtor${r !== 1 ? "s" : ""} with matching names`;
    }
    case "names": {
      const total = (c.name_nickname||0)+(c.name_business_suffix||0)+(c.name_all_caps||0)+(c.name_designation_tail||0)+(c.name_punct||0);
      return `${total} record${total !== 1 ? "s" : ""} with name-parsing issues`;
    }
    case "manual": {
      const n = c.manual_outliers || 0;
      return `${n} edge case${n !== 1 ? "s" : ""} flagged for review`;
    }
    case "social_links":
      return "Link shape fixes, suspect URLs, and unfixable entries";
    case "social_dupes":
      return "Social URLs shared by multiple realtors — review verified ownership";
    default:
      return "";
  }
}

function SummaryStrip({ counts, total, runAt }) {
  const tiles = [
    { label: "Total realtors", value: total ?? "—", accent: "slate" },
    { label: "Garbage", value: (counts.garbage_email_rows || 0) + (counts.garbage_test_brokerage_rows || 0), accent: "rose" },
    { label: "Phone dupes", value: counts.phone_dupe_groups || 0, accent: "amber" },
    { label: "Name dupes", value: counts.name_dupe_groups || 0, accent: "emerald" },
    { label: "Name fixes", value: (counts.name_nickname||0)+(counts.name_business_suffix||0)+(counts.name_all_caps||0)+(counts.name_designation_tail||0)+(counts.name_punct||0), accent: "violet" },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="cleanup-summary-strip">
      {tiles.map((t) => (
        <Card key={t.label} className="p-4">
          <div className="text-xs uppercase tracking-wide text-slate-500">{t.label}</div>
          <div className="text-2xl font-semibold mt-1 text-slate-900">{t.value}</div>
        </Card>
      ))}
      <div className="col-span-2 sm:col-span-3 lg:col-span-5 text-xs text-slate-500">
        Last scan: {runAt ? new Date(runAt).toLocaleString() : "—"}
      </div>
    </div>
  );
}
